import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, Typography, IconButton } from '@mui/material';
import { ReactComponent as CloseIcon } from 'assets/cross.icon.svg';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ThunkGetTicketTemplates, ThunkGetTicketTemplate } from 'store/ticketTemplates/thunks';
import { PortalConfigSelector } from 'store/config/selectors';
import { UserRoleSelector } from 'store/user/selector';
import { ThunkClientPortalCompanies } from 'store/ticketTemplates/thunks/clientPortalCompanies';
import { ThunkClientPortalConfigurations } from 'store/ticketTemplates/thunks/clientPortalConfigurationsOptions';

import { ITicketTemplate } from 'services/ticketTemplates/types';
import {
  InputType,
  REWST_TEMPLATE_INPUT_TYPE,
  REWST_TEMPLATE_TYPE,
} from 'constants/ticketTemplates';

import Loader from 'components/Loader';
import Button from 'components/Button/Button';
import TemplateHandler from 'components/CreateTicketDialog/components/TemplateHandler';
import RewestIframe from 'pages/TicketTemplate/components/RewestIframe';

import { LOCAL_STORAGE_PSA, LOCAL_STORAGE_TEAMS_CLIENT_TYPE } from 'constants/storage';
import { Roles } from 'constants/roles';

import useStyles from './styles';

interface IProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateTicketDialog: React.FC<IProps> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [ticketTemplates, setTicketTemplates] = useState<ITicketTemplate[]>([]);
  const [selectedTicketTemplate, setSelectedTicketTemplate] = useState<ITicketTemplate | undefined>(
    undefined,
  );
  const [rewestUrl, setRewestUrl] = useState<string>('');
  const [loadingRewestTemplate, setLoadingRewestTemplate] = useState(false);

  const dispatch = useAppDispatch();
  const { defaultTicketTemplateName, useDefaultTicketTemplate } =
    useAppSelector(PortalConfigSelector);
  const classes = useStyles();
  const role = useAppSelector(UserRoleSelector);

  const defaultTemplate: ITicketTemplate = useMemo(
    () => ({
      id: 0,
      title: defaultTicketTemplateName,
      type: 2,
      questions: [],
      value: 'Other',
    }),
    [defaultTicketTemplateName],
  );

  const isConnectWise = useMemo(
    () => localStorage.getItem(LOCAL_STORAGE_PSA) === 'Connectwise',
    [],
  );

  const onSelectedTicketTemplate = useCallback((template: ITicketTemplate) => {
    const isRewstTemplate =
      template.type === REWST_TEMPLATE_TYPE ||
      template.questions.some(q => q.inputType === REWST_TEMPLATE_INPUT_TYPE);

    if (isRewstTemplate) {
      const rewstUrl = template?.questions[0].values?.[0] ?? '';
      window.open(rewstUrl, '_blank');
    } else {
      setSelectedTicketTemplate(template);
    }
  }, []);

  const notUserRole = useMemo(() => role !== Roles.User, [role]);

  const onMount = useCallback(() => {
    setLoading(true);
    // For User role we want to load configs to show them in case ticket template ask for it
    // For all other roles we want to load companies associated with this customer
    if (isConnectWise) {
      if (notUserRole) {
        dispatch(ThunkClientPortalCompanies());
      } else {
        dispatch(ThunkClientPortalConfigurations());
      }
    }
    dispatch(ThunkGetTicketTemplates())
      .unwrap()
      .then(res => {
        setTicketTemplates(useDefaultTicketTemplate ? [...res, defaultTemplate] : res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    onMount();
  }, [onMount]);

  // Fetch Rewest template details when a Rewest template is selected
  useEffect(() => {
    const isRewest = (selectedTicketTemplate?.type as number) === REWST_TEMPLATE_TYPE;
    if (isRewest && selectedTicketTemplate?.id) {
      setLoadingRewestTemplate(true);
      dispatch(ThunkGetTicketTemplate({ ticketTemplateId: selectedTicketTemplate.id }))
        .unwrap()
        .then(res => {
          // Find the Rewest question and extract the URL
          const rewestQuestion = res.questions.find(q => q.inputType === InputType.Rewest);
          if (rewestQuestion?.values && rewestQuestion.values.length > 0) {
            setRewestUrl(rewestQuestion.values[0]);
          }
        })
        .finally(() => {
          setLoadingRewestTemplate(false);
        });
    } else {
      setRewestUrl('');
    }
  }, [selectedTicketTemplate, dispatch]);

  const isRewestTemplate = (selectedTicketTemplate?.type as number) === REWST_TEMPLATE_TYPE;

  const handleCloseRewest = (): void => {
    setSelectedTicketTemplate(undefined);
    setRewestUrl('');
  };

  const handleCloseDialog = (): void => {
    setSelectedTicketTemplate(undefined);
    setRewestUrl('');
    onClose();
  };

  return (
    <>
      {/* Full-screen Rewest overlay */}
      <Dialog
        open={open && isRewestTemplate && !!rewestUrl}
        onClose={handleCloseRewest}
        maxWidth={false}
        fullWidth
        className={classes.rewestDialog}
      >
        <DialogTitle className={classes.rewestDialogTitle}>
          <Typography variant="h6">{selectedTicketTemplate?.title ?? 'Rewest Form'}</Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseRewest}
            className={classes.closeIconButtonRewest}
          >
            <CloseIcon className={classes.closeButton} />
          </IconButton>
        </DialogTitle>
        <div className={classes.rewestFullScreenContainer}>
          {loadingRewestTemplate ? (
            <div className={classes.loaderWrapper}>
              <Loader />
            </div>
          ) : (
            <RewestIframe url={rewestUrl} title={selectedTicketTemplate?.title ?? 'Rewest Form'} />
          )}
        </div>
      </Dialog>

      {/* Regular dialog for non-Rewest templates */}
      <Dialog
        open={open && (!isRewestTemplate || !rewestUrl)}
        onClose={handleCloseDialog}
        className={classes.regularDialog}
      >
        <DialogTitle>
          <Typography>Create Ticket</Typography>
        </DialogTitle>
        <div className={classes.dialogBody}>
          {loading ? (
            <div className={classes.loaderWrapper}>
              <Loader />
              <div className={classes.buttonsContainer}>
                <Button text="Close" onClick={handleCloseDialog} />
              </div>
            </div>
          ) : (
            // Show list of ticket template or selected ticket template
            // TODO: add default ticket creation form
            // TODO: add company and config files creation for CW
            <>
              {selectedTicketTemplate ? (
                <TemplateHandler
                  ticketTemplateId={selectedTicketTemplate.id}
                  onClose={handleCloseDialog}
                  onSuccess={onSuccess}
                  isConnectWise={isConnectWise}
                  notUserRole={notUserRole}
                />
              ) : (
                <>
                  <div className={classes.templatesList}>
                    {ticketTemplates.map(template => (
                      <div
                        key={template.id}
                        className={classes.template}
                        onClick={() => {
                          onSelectedTicketTemplate(template);
                        }}
                      >
                        {template.title}
                      </div>
                    ))}
                  </div>
                  <div className={classes.buttonsContainer}>
                    <Button text="Close" onClick={handleCloseDialog} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default CreateTicketDialog;
